import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AppBreadcrumbs from 'components/AppBreadcrumbs';
import PageLayout from 'layouts/PageLayout';
import NotFound from 'pages/NotFound';
import { useAsyncFn } from 'react-use';
import { makeStyles } from '@material-ui/core/styles';
import { getCompany, updateCompany } from 'http/companies';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { routesAliases } from 'router/routesAliases';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ROLES } from 'appConstants';
import CompanyForm from '../Form';
import RolesAndTrainingModulesForm from '../Form/RolesAndTrainingModulesForm';
import ProvisionedRolesList from '../Form/ProvisionedRolesList';
import ProvisionedModulesList from '../Form/ProvisionedModulesList';
import VerticalTabs from 'components/VerticalTabs';

const useStyles = makeStyles(theme => ({
  pageContent: {
    padding: theme.spacing(5, 5),
  },
}));

const Edit = ({ routeProps }) => {
  const classes = useStyles();
  const history = useHistory();
  const routerMatch = useRouteMatch();
  const { t } = useTranslation();
  const { id } = routerMatch.params;
  const { profile } = useSelector(state => state.auth);
  const userRole = profile && profile.role;
  const isHyperAdmin = userRole === ROLES.HYPER;
  const [notFoundError, setNotFoundError] = useState(false);
  const [initialFormState, setInitialFormState] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('companyEditActiveTab');
    if (savedTab !== null) {
      const tabIndex = parseInt(savedTab, 10);
      localStorage.removeItem('companyEditActiveTab');
      return tabIndex;
    }
    return 0;
  });

  const breadCrumbsItems = [
    {
      title: t(routeProps.parentTitle),
      link: routeProps.parentPath,
    },
    {
      title: t(routeProps.title),
      isActive: true,
    },
  ];

  const [fetchUserState, fetchUserData] = useAsyncFn(async () => {
    const companyData = await getCompany(id);
    if (!companyData.error) {
      setInitialFormState({
        ...companyData,
        SAfirstName: companyData.superAdministrator ? companyData.superAdministrator.firstName : '',
        SAlastName: companyData.superAdministrator ? companyData.superAdministrator.lastName : '',
        SAemail: companyData.superAdministrator ? companyData.superAdministrator.email : '',
      });
    } else if (companyData.statusCode === 404) {
      setNotFoundError(true);
      return false;
    }
    return companyData;
  });

  const [submittingState, editCompany] = useAsyncFn(async formData => {
    const data = await updateCompany(formData, id);
    if (!data.error) {
      history.push(routesAliases.companiesList);
    }
    return data;
  }, []);

  useEffect(() => {
    fetchUserData(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event, { tabIndex }) => {
    setActiveTab(tabIndex);
  };

  const companyId = parseInt(id, 10);
  const provisionedRolesTabIndex = 2;
  const provisionedModulesTabIndex = 3;

  const tabsData = [
    {
      label: t('company.settings'),
      alias: 'settings',
      Component: (
        <CompanyForm
          isLoading={submittingState.loading}
          onSubmit={editCompany}
          companyData={initialFormState}
          routesAliases={routesAliases}
        />
      ),
    },
  ];

  if (isHyperAdmin) {
    tabsData.push(
      {
        label: t('company.rolesAndTrainingModules'),
        alias: 'rolesAndTrainingModules',
        Component: <RolesAndTrainingModulesForm companyId={companyId} routesAliases={routesAliases} />,
      },
      {
        label: t('company.provisionedRoles'),
        alias: 'provisionedRoles',
        Component: <ProvisionedRolesList companyId={companyId} returnTab={provisionedRolesTabIndex} />,
      },
      {
        label: t('company.provisionedModules'),
        alias: 'provisionedModules',
        Component: <ProvisionedModulesList companyId={companyId} returnTab={provisionedModulesTabIndex} />,
      },
    );
  }

  return notFoundError ? (
    <NotFound />
  ) : (
    <PageLayout
      pageTitle={<AppBreadcrumbs items={breadCrumbsItems} />}
      classes={{ content: classes.pageContent }}
      showLoader={fetchUserState.loading}
    >
      {isHyperAdmin && tabsData.length > 1 ? (
        <VerticalTabs tabsData={tabsData} activeTab={activeTab} onTabChange={handleTabChange} />
      ) : (
        <CompanyForm
          isLoading={submittingState.loading}
          onSubmit={editCompany}
          companyData={initialFormState}
          routesAliases={routesAliases}
        />
      )}
    </PageLayout>
  );
};

Edit.propTypes = {
  routeProps: PropTypes.shape({
    parentPath: PropTypes.string,
    parentTitle: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

export default Edit;
